import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Send, ShieldCheck, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { PageContainer } from '../../../common/components/PageContainer';
import { AdAudience, AdConnection, AudiencePreview, AudienceSource, useAdsApi } from '../../../common/hooks/use-ads-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { ConnectFirst, PageHeading, ProviderMark, formatNumber, readApiError, useAdConnections } from './ads-shared';

const AudiencesBody = () => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const connections = useAdConnections();
    const [previews, setPreviews] = useState<AudiencePreview[]>([]);
    const [audiences, setAudiences] = useState<AdAudience[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState<AudienceSource | null>(null);
    const [pushing, setPushing] = useState<AdAudience | null>(null);
    const [removing, setRemoving] = useState<AdAudience | null>(null);

    const sourceText: Record<string, { title: string; description: string }> = {
        leads: {
            title: t('ADS.AUDIENCES.SOURCE_LEADS', 'Everyone who left their details'),
            description: t('ADS.AUDIENCES.SOURCE_LEADS_DESC', 'Your customers and enquiries. The cheapest people to reach again, and the best seed for a lookalike.'),
        },
        visualized_not_quoted: {
            title: t('ADS.AUDIENCES.SOURCE_VISUALIZED', 'Tried a product, never asked for a price'),
            description: t('ADS.AUDIENCES.SOURCE_VISUALIZED_DESC', 'Put one of your products in their own room and stopped there. They liked it enough to try it.'),
        },
        asked_not_quoted: {
            title: t('ADS.AUDIENCES.SOURCE_ASKED', 'Talked to your Shopping Assistant'),
            description: t('ADS.AUDIENCES.SOURCE_ASKED_DESC', 'Asked a question about a product. Only those who also left their details can be reached.'),
        },
        all_engaged: {
            title: t('ADS.AUDIENCES.SOURCE_ENGAGED', 'Everyone who tried a product'),
            description: t('ADS.AUDIENCES.SOURCE_ENGAGED_DESC', 'Every customer who used the visualizer, whatever happened next.'),
        },
        product_interest: {
            title: t('ADS.AUDIENCES.SOURCE_PRODUCT', 'Interested in chosen products'),
            description: t('ADS.AUDIENCES.SOURCE_PRODUCT_DESC', 'Customers who tried specific products.'),
        },
    };

    const load = async () => {
        setLoading(true);
        try {
            const [p, a] = await Promise.all([api.previewAudiences(90), api.getAudiences()]);
            setPreviews(p);
            setAudiences(a);
        } catch (error) {
            console.error('Error loading audiences:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (connections.hasConnection) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connections.hasConnection]);

    const remove = async () => {
        if (!removing) return;
        try {
            await api.removeAudience(removing.uuid);
            toast.success(t('ADS.AUDIENCES.REMOVED', 'Removed here. It stays in your ad account in case a campaign uses it.'));
            setRemoving(null);
            load();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.AUDIENCES.REMOVE_FAILED', 'It could not be removed.')));
        }
    };

    const heading = (
        <PageHeading
            title={t('ADS.AUDIENCES.TITLE', 'Audiences')}
            subtitle={t('ADS.AUDIENCES.SUBTITLE', 'Groups built from what your customers did on your site — something no ad platform can see — sent to Meta or Google to reach them again.')}
        />
    );

    if (connections.loading) return <div className="flex flex-col gap-6">{heading}<Skeleton className="h-64 w-full" /></div>;
    if (!connections.hasConnection) return <div className="flex flex-col gap-6">{heading}<ConnectFirst /></div>;

    const connected = (connections.data?.connections ?? []).filter((c) => c.status === 'connected');

    return (
        <div className="flex flex-col gap-6">
            {heading}

            <Alert>
                <ShieldCheck />
                <AlertDescription>
                    {t('ADS.AUDIENCES.PRIVACY', 'Only a scrambled form of each email address is sent — never names, phone numbers or what people looked at. The list is rebuilt from your customers every time, so someone who asks to be deleted leaves the audience too.')}
                </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {loading
                    ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)
                    : previews.map((preview) => (
                        <Card key={preview.source}>
                            <CardHeader>
                                <CardTitle>{sourceText[preview.source]?.title}</CardTitle>
                                <CardDescription>{sourceText[preview.source]?.description}</CardDescription>
                                <CardAction>
                                    <span className="font-heading text-2xl font-medium tabular-nums">{formatNumber(preview.size)}</span>
                                </CardAction>
                            </CardHeader>
                            <CardFooter className="justify-between gap-2">
                                <span className="text-xs text-muted-foreground">
                                    {preview.usable
                                        ? t('ADS.AUDIENCES.WINDOW', 'Active in the last {{days}} days', { days: preview.windowDays })
                                        : t('ADS.AUDIENCES.TOO_SMALL', 'Platforms need at least {{min}} people', { min: preview.minimum })}
                                </span>
                                <Button variant="outline" size="sm" onClick={() => setCreating(preview.source)}>
                                    <Plus data-icon="inline-start" />
                                    {t('ADS.AUDIENCES.CREATE', 'Create audience')}
                                </Button>
                            </CardFooter>
                        </Card>
                    ))}
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{t('ADS.AUDIENCES.YOURS', 'Your audiences')}</CardTitle>
                    <CardDescription>{t('ADS.AUDIENCES.YOURS_DESC', 'Send one to an ad account, then pick it when you build a campaign there.')}</CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? <Skeleton className="h-24 w-full" /> : audiences.length ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('ADS.AUDIENCES.NAME', 'Name')}</TableHead>
                                    <TableHead>{t('ADS.AUDIENCES.BUILT_FROM', 'Built from')}</TableHead>
                                    <TableHead>{t('ADS.AUDIENCES.SENT_TO', 'Sent to')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.AUDIENCES.PEOPLE_SENT', 'People sent')}</TableHead>
                                    <TableHead className="w-40"><span className="sr-only">{t('ADS.COMMON.ACTIONS', 'Actions')}</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {audiences.map((a) => (
                                    <TableRow key={a.uuid}>
                                        <TableCell>
                                            <span className="flex flex-col">
                                                <span>{a.name}</span>
                                                {a.lastError && <span className="text-xs text-destructive">{a.lastError}</span>}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">{sourceText[a.source]?.title}</TableCell>
                                        <TableCell>
                                            {a.provider ? (
                                                <span className="flex items-center gap-2">
                                                    <ProviderMark provider={a.provider} withName />
                                                    <AudienceStatus status={a.status} />
                                                </span>
                                            ) : <span className="text-muted-foreground">{t('ADS.AUDIENCES.NOT_SENT', 'Not sent yet')}</span>}
                                        </TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(a.matchedCount)}</TableCell>
                                        <TableCell>
                                            <span className="flex justify-end gap-2">
                                                <Button variant="outline" size="sm" onClick={() => setPushing(a)} disabled={!connected.length}>
                                                    <Send data-icon="inline-start" />
                                                    {a.status === 'synced' ? t('ADS.AUDIENCES.UPDATE', 'Update') : t('ADS.AUDIENCES.SEND', 'Send')}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="icon-sm"
                                                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                                    aria-label={t('ADS.AUDIENCES.REMOVE', 'Remove')}
                                                    onClick={() => setRemoving(a)}
                                                >
                                                    <Trash2 />
                                                </Button>
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
                            <Users className="size-5" />
                            {t('ADS.AUDIENCES.NONE', 'No audiences yet. Create one from a group above.')}
                        </div>
                    )}
                </CardContent>
            </Card>

            <CreateDialog
                source={creating}
                titles={sourceText}
                onClose={() => setCreating(null)}
                onCreated={() => { setCreating(null); load(); }}
            />
            <PushDialog
                audience={pushing}
                connections={connected}
                onClose={() => setPushing(null)}
                onDone={() => { setPushing(null); load(); }}
            />

            <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('ADS.AUDIENCES.REMOVE_TITLE', 'Remove “{{name}}”?', { name: removing?.name })}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('ADS.AUDIENCES.REMOVE_BODY', 'It is removed from this list only. A copy already sent stays in your ad account, where a live campaign may be using it.')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('ADS.COMMON.CANCEL', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            className="border border-destructive bg-transparent text-destructive hover:bg-destructive/10"
                            onClick={(e) => { e.preventDefault(); remove(); }}
                        >
                            {t('ADS.AUDIENCES.REMOVE', 'Remove')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const AudienceStatus = ({ status }: { status: string }) => {
    const { t } = useTranslation();
    if (status === 'failed') return <Badge variant="destructive">{t('ADS.AUDIENCES.FAILED', 'Failed')}</Badge>;
    if (status === 'syncing') return <Badge variant="outline">{t('ADS.AUDIENCES.SENDING', 'Sending')}</Badge>;
    if (status === 'synced') return <Badge variant="secondary">{t('ADS.AUDIENCES.SENT', 'Sent')}</Badge>;
    return null;
};

const CreateDialog = ({
    source, titles, onClose, onCreated,
}: {
    source: AudienceSource | null;
    titles: Record<string, { title: string }>;
    onClose: () => void;
    onCreated: () => void;
}) => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const [name, setName] = useState('');
    const [windowDays, setWindowDays] = useState('90');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (source) setName(titles[source]?.title ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    const create = async () => {
        if (!source || !name.trim()) return;
        setBusy(true);
        try {
            await api.createAudience({ name: name.trim(), source, windowDays: Number(windowDays) });
            toast.success(t('ADS.AUDIENCES.CREATED', 'Audience created. Send it to an ad account to use it.'));
            onCreated();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.AUDIENCES.CREATE_FAILED', 'The audience could not be created.')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!source} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('ADS.AUDIENCES.CREATE', 'Create audience')}</DialogTitle>
                    <DialogDescription>{source ? titles[source]?.title : ''}</DialogDescription>
                </DialogHeader>
                <FieldGroup>
                    <Field>
                        <FieldLabel htmlFor="audience-name">{t('ADS.AUDIENCES.NAME', 'Name')}</FieldLabel>
                        <Input id="audience-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
                        <FieldDescription>{t('ADS.AUDIENCES.NAME_HINT', 'What you will pick from in the ad account.')}</FieldDescription>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="audience-window">{t('ADS.AUDIENCES.LOOK_BACK', 'Look back')}</FieldLabel>
                        <NativeSelect id="audience-window" value={windowDays} onChange={(e) => setWindowDays(e.target.value)}>
                            {[30, 90, 180, 365].map((d) => (
                                <NativeSelectOption key={d} value={String(d)}>{t('ADS.AUDIENCES.DAYS', '{{days}} days', { days: d })}</NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </Field>
                </FieldGroup>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>{t('ADS.COMMON.CANCEL', 'Cancel')}</Button>
                    <Button onClick={create} disabled={busy || !name.trim()}>
                        {busy && <Spinner data-icon="inline-start" />}
                        {t('ADS.AUDIENCES.CREATE', 'Create audience')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const PushDialog = ({
    audience, connections, onClose, onDone,
}: { audience: AdAudience | null; connections: AdConnection[]; onClose: () => void; onDone: () => void }) => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const [target, setTarget] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setTarget(audience?.connectionUuid ?? connections[0]?.uuid ?? '');
    }, [audience, connections]);

    const push = async () => {
        if (!audience || !target) return;
        setBusy(true);
        try {
            const result = await api.pushAudience(audience.uuid, target);
            toast.success(`${t('ADS.AUDIENCES.PUSHED', '{{count}} people sent.', { count: result.sent })} ${result.note ?? ''}`);
            onDone();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.AUDIENCES.PUSH_FAILED', 'The audience could not be sent.')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!audience} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('ADS.AUDIENCES.SEND_TITLE', 'Send “{{name}}”', { name: audience?.name })}</DialogTitle>
                    <DialogDescription>{t('ADS.AUDIENCES.SEND_BODY', 'The list is rebuilt from your customers now and sent as scrambled email addresses.')}</DialogDescription>
                </DialogHeader>
                <FieldGroup>
                    <Field>
                        <FieldLabel htmlFor="audience-target">{t('ADS.AUDIENCES.ACCOUNT', 'Ad account')}</FieldLabel>
                        <NativeSelect id="audience-target" value={target} onChange={(e) => setTarget(e.target.value)}>
                            {connections.map((c) => (
                                <NativeSelectOption key={c.uuid} value={c.uuid}>
                                    {`${c.provider === 'google' ? 'Google' : 'Meta'} · ${c.accountName}`}
                                </NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </Field>
                </FieldGroup>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>{t('ADS.COMMON.CANCEL', 'Cancel')}</Button>
                    <Button onClick={push} disabled={busy || !target}>
                        {busy && <Spinner data-icon="inline-start" />}
                        {t('ADS.AUDIENCES.SEND', 'Send')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const AdsAudiencesPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.ADS.TITLE', 'Ads') }, { label: t('SIDEBAR.ADS.AUDIENCES', 'Audiences') }]}>
            <FeatureGate feature="adsManager">
                <AudiencesBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AdsAudiencesPage;
